import { UserConfigDto } from "@lib/ShiOneClient";
import React, { useEffect, useLayoutEffect, useRef } from "react";
import { GridApi, GridEvents } from "@mui/x-data-grid-pro";
import { useSettingsHostPathname } from "../../v2/src/hooks/useSettingsHostPathname";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import {
  cacheKey as userConfigsCacheKey,
  useUpsertUserConfig,
  useDeleteUserConfig,
} from "../../modules/hooks/useUserConfig";
import { isDefaultGridState } from "./gridStateUtils";

const SAVE_EVENTS: GridEvents[] = [
  "columnVisibilityModelChange",
  "columnOrderChange",
  "sortModelChange",
  "columnResizeStop",
  "pinnedColumnsChange",
  "paginationModelChange",
];

// `null` means the settings key should not exist (the grid is in its default state).
type SettingsValue = string | null;
// `confirmed` is the last value the server is known to hold; `undefined` means it is not
// known yet because the configs had not loaded when the first write started.
type PendingWrite = { latest: SettingsValue; confirmed: SettingsValue | undefined };

// Writes per settings key are serialized here and the latest requested value is
// reconciled once the in-flight write settles.
const pendingWritesByClient = new WeakMap<QueryClient, Map<string, PendingWrite>>();

const findSettingsValue = (
  configs: UserConfigDto[] | undefined,
  settingsKey: string
): SettingsValue | undefined =>
  configs &&
  (configs.find((config) => config.settingsKey === settingsKey)?.settingsValue ?? null);

// The cached configs mirror the latest requested value so a grid that remounts mid-save
// restores the user's latest choice instead of the last value the server confirmed.
const cacheSettingsValue = (
  queryClient: QueryClient,
  settingsKey: string,
  value: SettingsValue
) => {
  queryClient.setQueryData<UserConfigDto[]>([userConfigsCacheKey], (configs) => {
    if (!configs) return configs;
    const current = configs.find((config) => config.settingsKey === settingsKey);
    if ((current?.settingsValue ?? null) === value) return configs;
    if (value === null) {
      return configs.filter((config) => config.settingsKey !== settingsKey);
    }
    const config = new UserConfigDto();
    config.settingsKey = settingsKey;
    config.settingsValue = value;
    return current
      ? configs.map((item) => (item === current ? config : item))
      : [...configs, config];
  });
};

const getPendingWrites = (queryClient: QueryClient) => {
  let pendingWrites = pendingWritesByClient.get(queryClient);
  if (!pendingWrites) {
    const writes = new Map<string, PendingWrite>();
    pendingWrites = writes;
    pendingWritesByClient.set(queryClient, writes);
    // A refetch while a write is in flight can return the value the write replaces.
    queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type !== "updated" ||
        event.action.type !== "success" ||
        event.action.manual ||
        event.query.queryKey[0] !== userConfigsCacheKey
      ) {
        return;
      }
      const fetchedConfigs = event.query.state.data as UserConfigDto[] | undefined;
      writes.forEach((write, settingsKey) => {
        write.confirmed ??= findSettingsValue(fetchedConfigs, settingsKey);
        cacheSettingsValue(queryClient, settingsKey, write.latest);
      });
    });
  }
  return pendingWrites;
};

const getPageSize = (gridState: unknown): number | undefined =>
  (gridState as { pagination?: { paginationModel?: { pageSize?: number } } } | undefined)
    ?.pagination?.paginationModel?.pageSize;

const getPersistedPageSize = (
  persistedValue: SettingsValue,
  defaultInitialState: unknown
): number | undefined => {
  if (persistedValue === null) return getPageSize(defaultInitialState);
  try {
    return (
      getPageSize(JSON.parse(persistedValue).gridState) ??
      getPageSize(defaultInitialState)
    );
  } catch {
    return undefined;
  }
};

export function useRegisterDataGridSaveEvents(
  apiRef: React.RefObject<GridApi | null>,
  gridId: string | undefined,
  saveUserConfig: boolean,
  defaultInitialState: unknown,
  isRestoringRef: React.MutableRefObject<boolean>
) {
  const hostPathname = useSettingsHostPathname();
  const queryClient = useQueryClient();
  const { mutateAsync: upsertUserConfig } = useUpsertUserConfig();
  const { mutateAsync: deleteUserConfig } = useDeleteUserConfig();

  const writeSettings = (
    pendingWrites: Map<string, PendingWrite>,
    settingsKey: string,
    value: SettingsValue
  ) => {
    const pendingWrite: PendingWrite = {
      latest: value,
      confirmed: findSettingsValue(
        queryClient.getQueryData<UserConfigDto[]>([userConfigsCacheKey]),
        settingsKey
      ),
    };
    pendingWrites.set(settingsKey, pendingWrite);

    const send = async (sentValue: SettingsValue): Promise<void> => {
      cacheSettingsValue(queryClient, settingsKey, sentValue);
      const config = new UserConfigDto();
      config.settingsKey = settingsKey;
      let failed = false;
      try {
        if (sentValue === null) {
          await deleteUserConfig(config);
        } else {
          config.settingsValue = sentValue;
          await upsertUserConfig(config);
        }
      } catch {
        // Failures surface through the mutation state; still reconcile the latest value.
        failed = true;
      }
      if (!failed) {
        pendingWrite.confirmed = sentValue;
      }
      if (pendingWrite.latest !== sentValue) {
        return send(pendingWrite.latest);
      }
      pendingWrites.delete(settingsKey);
      if (failed) {
        // Roll back the rejected optimistic value now: a grid that remounts before the
        // refetch settles restores from the cache and keeps that value.
        if (pendingWrite.confirmed !== undefined) {
          cacheSettingsValue(queryClient, settingsKey, pendingWrite.confirmed);
        }
        void queryClient.invalidateQueries({ queryKey: [userConfigsCacheKey] });
      }
    };

    void send(value);
  };

  const saveGridState = (event: GridEvents) => {
    if (!apiRef?.current || !saveUserConfig) return;
    if (isRestoringRef?.current) return;
    const state = apiRef.current.exportState();

    const settingsKey =
      hostPathname + "_MuiGridSettings" + (gridId ? ":" + gridId : "");
    const pendingWrites = getPendingWrites(queryClient);
    const pendingWrite = pendingWrites.get(settingsKey);
    // An unloaded cache is treated as "nothing saved" so no delete is sent before it loads.
    const persistedValue: SettingsValue = pendingWrite
      ? pendingWrite.latest
      : findSettingsValue(
          queryClient.getQueryData<UserConfigDto[]>([userConfigsCacheKey]),
          settingsKey
        ) ?? null;

    // Restore always starts on page 0, so the page number is not part of the saved view.
    // Rebuild the model with fixed key order: MUI emits `{ pageSize, page }` from
    // `setPageSize`, which would break the JSON comparisons below.
    const pageSize = state.pagination?.paginationModel?.pageSize;
    const cleanedState = {
      ...state,
      pagination:
        pageSize === undefined
          ? undefined
          : { paginationModel: { page: 0, pageSize } },
    };

    // Page size is the only persisted pagination value. This also ignores the deferred
    // event a controlled grid publishes after `useCXUserPrefs` restores the saved page size,
    // which arrives after `isRestoringRef` has been cleared.
    if (
      event === "paginationModelChange" &&
      pageSize === getPersistedPageSize(persistedValue, defaultInitialState)
    ) {
      return;
    }

    const desiredValue: SettingsValue = isDefaultGridState(
      defaultInitialState,
      cleanedState
    )
      ? null
      : JSON.stringify({ gridState: cleanedState });

    if (pendingWrite) {
      pendingWrite.latest = desiredValue;
      cacheSettingsValue(queryClient, settingsKey, desiredValue);
      return;
    }
    if (desiredValue === persistedValue) return;

    writeSettings(pendingWrites, settingsKey, desiredValue);
  };

  // A controlled `paginationModel` only publishes `paginationModelChange` from the grid's
  // own prop-sync effect. Re-subscribing on every render would drop that event, so the
  // subscriptions stay fixed and read the latest handler through a ref instead.
  const saveGridStateRef = useRef(saveGridState);
  useLayoutEffect(() => {
    saveGridStateRef.current = saveGridState;
  });

  // The shared grid is keyed by `gridId`, so a new ID remounts it behind the same `apiRef`.
  useEffect(() => {
    const api = apiRef?.current;
    if (!api) return;
    const unsubscribers = SAVE_EVENTS.map((event) =>
      api.subscribeEvent(event, () => saveGridStateRef.current(event))
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [apiRef, gridId]);
}
